import { useId, useRef, useState } from 'react';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import type { IpCountMode } from '../../apps/cidr-planner/types';
import './ModeToggle.css';

type ModeToggleProps = {
  mode: IpCountMode;
  onChange: (mode: IpCountMode) => void;
  lang: DisplayLang;
};

const TEXT = {
  ja: {
    label: 'IP カウント',
    standard: '標準',
    aws: 'AWS VPC',
    helpLabel: 'IP カウントの説明',
    tooltip: 'AWS VPC モードは AWS の予約アドレス数に合わせて計算しています。',
  },
  en: {
    label: 'IP count',
    standard: 'Standard',
    aws: 'AWS VPC',
    helpLabel: 'IP count explanation',
    tooltip: 'AWS VPC mode follows AWS subnet reserved address counts.',
  },
} as const;

export function ModeToggle({ mode, onChange, lang }: ModeToggleProps) {
  const t = TEXT[lang];
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipId = useId();
  const skipClickRef = useRef(false);

  const toggleTooltip = () => {
    setTooltipOpen((open) => !open);
  };

  const handleHelpPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    event.stopPropagation();
    skipClickRef.current = true;
    toggleTooltip();
  };

  const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    toggleTooltip();
  };

  const handleHelpKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    toggleTooltip();
  };

  return (
    <div className="cidr-mode-field">
      <div className="cidr-mode-field__label-row">
        <span className="cidr-mode-field__label">{t.label}</span>
        <button
          type="button"
          className="cidr-mode-field__help-btn"
          onPointerUp={handleHelpPointerUp}
          onClick={handleHelpClick}
          onKeyDown={handleHelpKeyDown}
          aria-expanded={tooltipOpen}
          aria-describedby={tooltipOpen ? tooltipId : undefined}
          aria-label={t.helpLabel}
        >
          <span className="cidr-mode-field__help-icon" aria-hidden>
            i
          </span>
        </button>
      </div>
      <div
        id={tooltipId}
        role="tooltip"
        className={`cidr-mode-field__tooltip${
          tooltipOpen ? ' cidr-mode-field__tooltip--open' : ''
        }`}
      >
        <p className="cidr-mode-field__tooltip-body">{t.tooltip}</p>
      </div>
      <div className="cidr-mode-toggle" role="group" aria-label={t.label}>
        <button
          type="button"
          className={`cidr-mode-toggle__btn${mode === 'standard' ? ' cidr-mode-toggle__btn--active' : ''}`}
          onClick={() => onChange('standard')}
        >
          {t.standard}
        </button>
        <button
          type="button"
          className={`cidr-mode-toggle__btn${mode === 'aws' ? ' cidr-mode-toggle__btn--active' : ''}`}
          onClick={() => onChange('aws')}
        >
          {t.aws}
        </button>
      </div>
    </div>
  );
}
